import { Component } from '@theme/component';

/**
 * A custom element that renders a video background.
 *
 * @typedef {object} Refs
 * @property {HTMLElement[]} videoSources - The video sources.
 * @property {HTMLVideoElement} videoElement - The video element.
 *
 * @extends Component<Refs>
 */
export class VideoBackgroundComponent extends Component {
  requiredRefs = ['videoSources', 'videoElement'];

  connectedCallback() {
    super.connectedCallback();

    const { videoSources, videoElement, playPauseBtn } = this.refs;

    for (const source of videoSources) {
      const { videoSource } = source.dataset;

      if (videoSource) source.setAttribute('src', videoSource);
    }

    videoElement.load();

    const btn = /** @type {HTMLButtonElement | undefined} */ (playPauseBtn);

    // Auto-pause after 5 seconds of playback
    /** @type {ReturnType<typeof setTimeout> | null} */
    let autoPauseTimer = null;

    videoElement.addEventListener('play', () => {
      if (autoPauseTimer) clearTimeout(autoPauseTimer);
      autoPauseTimer = setTimeout(() => {
        videoElement.pause();
        if (btn) {
          btn.dataset.state = 'paused';
          btn.setAttribute('aria-label', 'Play video');
        }
      }, 5000);
    });

    videoElement.addEventListener('pause', () => {
      if (autoPauseTimer) {
        clearTimeout(autoPauseTimer);
        autoPauseTimer = null;
      }
    });

    if (btn) {
      btn.addEventListener('click', () => {
        if (videoElement.paused) {
          videoElement.play();
          btn.dataset.state = 'playing';
          btn.setAttribute('aria-label', 'Pause video');
        } else {
          videoElement.pause();
          btn.dataset.state = 'paused';
          btn.setAttribute('aria-label', 'Play video');
        }
      });
    }
  }
}

if (!customElements.get('video-background-component')) {
  customElements.define('video-background-component', VideoBackgroundComponent);
}
